import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flappyAPI } from '../services/api';
import { flappyDuelSocket } from '../services/socket';
import FlappyCanvas from '../components/FlappyCanvas';
import { buildInviteLink } from '../utils/inviteLinks';
import QuickChat from '../components/QuickChat';
import '../styles/Flappy.css';

function asNumber(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

export default function FlappyMatch() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user, refreshUser } = useAuth();

  const [match, setMatch] = useState(null);
  const [roundData, setRoundData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [rechallenging, setRechallenging] = useState(false);
  const [acceptingRematch, setAcceptingRematch] = useState(false);
  const [pendingRematchId, setPendingRematchId] = useState(null);

  const [ghostEnabled, setGhostEnabled] = useState(false);

  const liveFlapsRef = useRef([]);
  const liveFlapsLenRef = useRef(0);
  const [liveGhost, setLiveGhost] = useState(null);
  const [liveError, setLiveError] = useState('');

  const [nowMs, setNowMs] = useState(() => Date.now());
  const inviteJoinAttemptedRef = useRef(false);

  const load = async () => {
    try {
      setError('');
      const mRes = await flappyAPI.getMatch(matchId);
      const m = mRes.data;
      setMatch(m);

      if (m?.status === 'completed' && Number(m.opponent_id) === Number(user?.id)) {
        try {
          const cRes = await flappyAPI.listChallenges();
          const list = cRes.data || [];
          const rematch = list.find((item) => (
            Number(item.creator_id) === Number(m.creator_id) &&
            Number(item.opponent_id) === Number(user?.id) &&
            item.status === 'pending' &&
            !item.accepted_at
          ));
          setPendingRematchId(rematch ? rematch.id : null);
        } catch {
          setPendingRematchId(null);
        }
      } else {
        setPendingRematchId(null);
      }

      const roundNum = asNumber(m.current_round || 1);
      const rRes = await flappyAPI.getRound(matchId, roundNum);
      setRoundData(rRes.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    // New match route should start from a clean local play state.
    setSubmitted(false);
    setSubmitting(false);
    setPendingRematchId(null);
    setLiveGhost(null);
    liveFlapsRef.current = [];
    liveFlapsLenRef.current = 0;
  }, [matchId]);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const v = localStorage.getItem('flappy:ghostEnabled');
      if (v === '0') setGhostEnabled(false);
      if (v === '1') setGhostEnabled(true);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('flappy:ghostEnabled', ghostEnabled ? '1' : '0');
    } catch {
      // ignore
    }
  }, [ghostEnabled]);

  const meId = asNumber(user?.id);
  const creatorId = asNumber(match?.creator_id);
  const opponentId = asNumber(match?.opponent_id);
  const isCreator = meId && creatorId && meId === creatorId;
  const isOpponent = meId && opponentId && meId === opponentId;

  const currentTurnPlayerId = useMemo(() => {
    if (!match) return null;
    return asNumber(match.current_turn) === 1 ? creatorId : opponentId;
  }, [match, creatorId, opponentId]);

  const currentRound = asNumber(match?.current_round || 1);
  const myTurn = currentTurnPlayerId && meId === currentTurnPlayerId;
  const status = String(match?.status || '');
  const canJoinOpenRoom = status === 'pending' && !match?.opponent_id && !isCreator && !isOpponent;

  const runs = roundData?.runs || [];
  const creatorRun = runs.find((r) => asNumber(r.player_id) === creatorId);
  const opponentRun = runs.find((r) => asNumber(r.player_id) === opponentId);

  const myRun = isCreator
    ? creatorRun
    : (isOpponent ? opponentRun : runs.find((r) => asNumber(r.player_id) === meId));

  const otherRun = isCreator
    ? opponentRun
    : (isOpponent ? creatorRun : runs.find((r) => asNumber(r.player_id) !== meId));

  useEffect(() => {
    const tryAutoJoinFromInvite = async () => {
      if (!canJoinOpenRoom) return;
      if (!match?.id) return;
      if (inviteJoinAttemptedRef.current) return;
      inviteJoinAttemptedRef.current = true;

      try {
        setError('');
        await flappyAPI.joinMatch(match.id);
        await load();
        refreshUser?.();
      } catch (e) {
        const msg = String(e?.response?.data?.error || '').toLowerCase();
        if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
          setError('This room is full. You can watch the game live.');
        } else {
          setError(e.response?.data?.error || 'Unable to join this room');
        }
      }
    };

    void tryAutoJoinFromInvite();
  }, [canJoinOpenRoom, match?.id, refreshUser]);

  // Join live duel room + listen for live flap updates
  useEffect(() => {
    if (!matchId) return;

    setLiveError('');
    flappyDuelSocket.join(matchId);

    const handleOpponentFlap = (payload = {}) => {
      try {
        if (String(payload.matchId) !== String(matchId)) return;
        if (Number(payload.roundNumber || 1) !== Number(currentRound || 1)) return;

        // Only use live flaps when it's NOT our turn and there's no stored run yet.
        if (myTurn) return;
        if (otherRun) return;

        const tMs = Number(payload.tMs);
        if (!Number.isFinite(tMs) || tMs < 0) return;

        liveFlapsRef.current.push(tMs);
      } catch {
        // ignore
      }
    };

    const handleRunEnded = (payload = {}) => {
      try {
        if (String(payload.matchId) !== String(matchId)) return;
        if (Number(payload.roundNumber || 1) !== Number(currentRound || 1)) return;

        // Update live ghost immediately to the final run.
        if (!myTurn && !otherRun) {
          setLiveGhost({
            player_id: payload.playerId,
            score: asNumber(payload.score),
            ended_at_ms: asNumber(payload.endedAtMs),
            ended_reason: payload.endedReason,
            flap_times: Array.isArray(payload.flapTimes) ? payload.flapTimes : [],
            duration_seconds: asNumber(match?.duration_seconds || 60),
          });
          liveFlapsRef.current = Array.isArray(payload.flapTimes) ? payload.flapTimes.slice(0) : [];
          liveFlapsLenRef.current = liveFlapsRef.current.length;
        }
        // Pull latest match state (turn flips / completion)
        load();
        refreshUser?.();
      } catch {
        // ignore
      }
    };

    const handleAccepted = (payload = {}) => {
      try {
        if (String(payload.matchId) !== String(matchId)) return;
        load();
        refreshUser?.();
      } catch {
        // ignore
      }
    };

    const handleStarted = (payload = {}) => {
      try {
        if (String(payload.matchId) !== String(matchId)) return;
        load();
      } catch {
        // ignore
      }
    };

    const handleRematchCreated = (payload = {}) => {
      try {
        if (String(payload.originalMatchId) !== String(matchId)) return;
        const incomingRematchId = Number(payload.newMatchId || 0);
        if (!incomingRematchId) return;

        // Opponent on completed original match can accept rematch immediately.
        if (status === 'completed' && isOpponent) {
          setPendingRematchId(incomingRematchId);
        }

        // Pull latest state for both players in the original match view.
        load();
      } catch {
        // ignore
      }
    };

    flappyDuelSocket.onOpponentFlap(handleOpponentFlap);
    flappyDuelSocket.onRunEnded(handleRunEnded);
    flappyDuelSocket.onAccepted(handleAccepted);
    flappyDuelSocket.onStarted(handleStarted);
    flappyDuelSocket.onRematchCreated(handleRematchCreated);

    const pumpId = setInterval(() => {
      // Throttle UI updates: rebuild ghostRun only occasionally.
      try {
        if (myTurn) return;
        if (otherRun) return;
        if (match?.status !== 'in_progress') return;

        const len = liveFlapsRef.current.length;
        if (len === liveFlapsLenRef.current) return;
        liveFlapsLenRef.current = len;

        const durationMs = Math.max(10, asNumber(match?.duration_seconds || 60)) * 1000;
        setLiveGhost((prev) => ({
          ...(prev || {}),
          score: prev?.score ?? 0,
          ended_at_ms: prev?.ended_at_ms ?? durationMs,
          flap_times: liveFlapsRef.current.slice(0),
        }));
      } catch {
        // ignore
      }
    }, 200);

    return () => {
      clearInterval(pumpId);
      flappyDuelSocket.removeListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, currentRound, myTurn, status, isOpponent]);

  useEffect(() => {
    // Reset live ghost buffers when round/turn changes.
    liveFlapsRef.current = [];
    liveFlapsLenRef.current = 0;
    setLiveGhost(null);
  }, [currentRound, match?.current_turn]);

  useEffect(() => {
    // If round advanced, allow playing again.
    setSubmitted(false);
    setSubmitting(false);
  }, [currentRound]);

  const handleFinish = async ({ score, endedReason, endedAtMs, flapTimes }) => {
    if (!match) return;
    if (!myTurn) return;
    if (submitted || submitting) return;

    try {
      setSubmitting(true);
      setError('');

      await flappyAPI.submitRun(match.id, currentRound, {
        score,
        endedReason,
        endedAtMs,
        flapTimes,
      });

      setSubmitted(true);
      refreshUser?.();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit run');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flappy-page">
        <div className="flappy-hint">Loading...</div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="flappy-page">
        <div className="flappy-error">Match not found</div>
        <button className="flappy-btn" onClick={() => navigate('/flappy/duel')}>Back to Lobby</button>
      </div>
    );
  }

  const durationSeconds = asNumber(match.duration_seconds || 60);

  const roundSeed = roundData?.round?.round_seed;

  const badge = (() => {
    if (status === 'pending') {
      if (!match.opponent_id) return 'Waiting for opponent to join...';
      if (!match.accepted_at) return isOpponent ? 'Challenge received' : 'Waiting for opponent to accept...';
      return isCreator ? 'Ready to start' : 'Waiting for creator to start...';
    }
    if (status === 'completed') return 'Match completed';
    return myTurn ? 'Your turn' : 'Waiting for opponent';
  })();

  const canCancel = status === 'pending' && isCreator && !match.started_at;
  const isRoomFullForViewer = status === 'pending' && !!match?.opponent_id && !isCreator && !isOpponent;
  const canClaimTimeout = status === 'in_progress' && !myTurn;

  const timeoutSeconds = asNumber(match.turn_timeout_seconds || 300);
  const lastRunCreatedAtMs = (() => {
    try {
      const createdAts = (roundData?.runs || [])
        .map((r) => (r?.created_at ? Date.parse(r.created_at) : NaN))
        .filter((t) => Number.isFinite(t));
      if (!createdAts.length) return null;
      return Math.max(...createdAts);
    } catch {
      return null;
    }
  })();

  const startedAtMs = match.started_at ? Date.parse(match.started_at) : null;
  const acceptedAtMs = match.accepted_at ? Date.parse(match.accepted_at) : null;
  const lastActivityMs = lastRunCreatedAtMs || startedAtMs || acceptedAtMs || null;
  const secondsSinceActivity = lastActivityMs ? Math.max(0, Math.floor((nowMs - lastActivityMs) / 1000)) : 0;
  const secondsUntilTimeout = lastActivityMs ? Math.max(0, timeoutSeconds - secondsSinceActivity) : timeoutSeconds;
  const timeoutReady = canClaimTimeout && secondsUntilTimeout <= 0;

  const winnerText = (() => {
    if (status !== 'completed') return '';
    const winnerId = match.winner_id ? asNumber(match.winner_id) : null;
    if (!winnerId) return 'Result: Tie';
    if (winnerId === meId) return 'Result: You win!';
    return 'Result: You lose';
  })();

  const payoutSummary = (() => {
    const wager = asNumber(match?.wager || 0);
    if (!wager) return null;
    const grossPot = wager * 2;
    const chargePercent = Number.isFinite(Number(match?.charge_percent))
      ? Number(match.charge_percent)
      : 7;
    const chargeAmount = Number.isFinite(Number(match?.charge_amount))
      ? Number(match.charge_amount)
      : Math.floor((grossPot * chargePercent) / 100);
    const netPot = Number.isFinite(Number(match?.net_pot))
      ? Number(match.net_pot)
      : Math.max(0, grossPot - chargeAmount);
    return { grossPot, chargePercent, chargeAmount, netPot };
  })();

  const bothScoresKnown = !!(creatorRun && opponentRun);
  const myScore = myRun ? asNumber(myRun.score) : null;
  const oppScore = otherRun ? asNumber(otherRun.score) : null;

  const effectiveGhostRun = (() => {
    // After a match completes, default the canvas replay/HUD to YOUR run so the
    // on-canvas scoreboard doesn't appear to "swap" to the opponent's score.
    if (status === 'completed') {
      return myRun || otherRun || null;
    }
    return ghostEnabled ? (otherRun || liveGhost || null) : null;
  })();

  const canRematch = status === 'completed' && isCreator;
  const canAcceptRematch = status === 'completed' && isOpponent && pendingRematchId;
  const inviteLink = match?.id ? buildInviteLink(`/flappy/duel/${match.id}`) : '';

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        window.prompt('Copy Flappy invite link:', inviteLink);
      }
    } catch {
      window.prompt('Copy Flappy invite link:', inviteLink);
    }
  };

  return (
    <div className="flappy-page">
      <div className="flappy-header">
        <div>
          <h1 className="flappy-title">🐦 Flappy Duel • Match #{match.id}</h1>
          <div className="flappy-subtitle">
            {badge} • Round {currentRound}/{match.rounds_total} • Score {match.creator_round_wins}-{match.opponent_round_wins}
          </div>
        </div>
        <div className="flappy-header-actions">
          <button className="flappy-btn" onClick={() => navigate('/flappy/duel')}>← Lobby</button>
          <button className="flappy-btn" onClick={load}>Refresh</button>
        </div>
      </div>

      {error && <div className="flappy-error">{error}</div>}
      {liveError && <div className="flappy-error">{liveError}</div>}
      <div className="flappy-grid">
        <section className="flappy-card">
          <h2>Match</h2>
          <div className="flappy-meta">
            <div><strong>Duration:</strong> {durationSeconds}s</div>
            {status === 'completed' && <div><strong>{winnerText}</strong></div>}
            
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="player-badge">
                <span className="player-name">{match.creator_username}</span>
                <span className="player-rating">⭐ {match.creator_rating || 1200}</span>
                {asNumber(match.creator_streak) >= 3 && (
                  <span className="cm-player-stat cm-player-stat--streak">🔥 {match.creator_streak}</span>
                )}
              </div>
              <div className="player-badge">
                <span className="player-name">{match.opponent_username || 'Waiting...'}</span>
                {match.opponent_id && (
                  <>
                    <span className="player-rating">⭐ {match.opponent_rating || 1200}</span>
                    {asNumber(match.opponent_streak) >= 3 && (
                      <span className="cm-player-stat cm-player-stat--streak">🔥 {match.opponent_streak}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {inviteLink && (
            <div className="match-invite-row" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button 
                className="flappy-btn-primary" 
                onClick={copyInviteLink} 
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', padding: '6px 12px' }}
                title="Copy Invite Link to clipboard"
              >
                📋 Copy Invite Link
              </button>
            </div>
          )}

          {status === 'completed' && payoutSummary && (
            <div className="flappy-hint" style={{ marginTop: 10 }}>
              <strong>Payout:</strong> Pot {payoutSummary.grossPot} • Charge {payoutSummary.chargeAmount} ({payoutSummary.chargePercent}%) • Net {payoutSummary.netPot}
            </div>
          )}

          {canCancel && (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                className="flappy-btn"
                onClick={async () => {
                  try {
                    setError('');
                    await flappyAPI.cancelMatch(match.id);
                    refreshUser?.();
                    navigate('/flappy/duel');
                  } catch (e) {
                    setError(e.response?.data?.error || 'Failed to cancel match');
                  }
                }}
              >
                Cancel Challenge
              </button>
              <div className="flappy-hint" style={{ marginTop: 6 }}>
                Allowed only before the match starts.
              </div>
            </div>
          )}

          {canClaimTimeout && (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                className="flappy-btn"
                disabled={!timeoutReady}
                onClick={async () => {
                  try {
                    setError('');
                    await flappyAPI.claimTimeoutWin(match.id);
                    await load();
                    refreshUser?.();
                  } catch (e) {
                    setError(e.response?.data?.error || 'Cannot claim timeout win yet');
                  }
                }}
              >
                Claim Win (Opponent Abandoned)
              </button>
              <div className="flappy-hint" style={{ marginTop: 6 }}>
                {timeoutReady
                  ? 'Opponent timed out. You can claim the win.'
                  : `You can claim in ${Math.ceil(secondsUntilTimeout / 60)} min (${secondsUntilTimeout}s).`}
              </div>
            </div>
          )}

          {status === 'completed' && (
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="flappy-btn-primary"
                disabled={!canRematch || rechallenging}
                onClick={async () => {
                  if (!canRematch) return;
                  try {
                    setRechallenging(true);
                    setError('');

                    const res = await flappyAPI.rechallenge(match.id);

                    refreshUser?.();
                    navigate(`/flappy/duel/${res.data.id}`);
                  } catch (e) {
                    setError(e.response?.data?.error || 'Failed to rechallenge');
                  } finally {
                    setRechallenging(false);
                  }
                }}
              >
                {rechallenging ? 'Creating...' : 'Rematch'}
              </button>

              <button
                className="flappy-btn-primary"
                disabled={!canAcceptRematch || acceptingRematch}
                onClick={async () => {
                  if (!canAcceptRematch) return;
                  try {
                    setAcceptingRematch(true);
                    setError('');

                    if (!pendingRematchId) {
                      setError('No rematch found yet. Ask your opponent to send one.');
                      return;
                    }

                    await flappyAPI.acceptMatch(pendingRematchId);
                    refreshUser?.();
                    navigate(`/flappy/duel/${pendingRematchId}`);
                  } catch (e) {
                    setError(e.response?.data?.error || 'Failed to accept rematch');
                  } finally {
                    setAcceptingRematch(false);
                  }
                }}
              >
                {acceptingRematch ? 'Accepting...' : 'Accept Rematch'}
              </button>

              <button className="flappy-btn" onClick={() => navigate('/flappy/duel')}>
                Back to Lobby
              </button>
            </div>
          )}

          {(status === 'completed' || bothScoresKnown) && (
            <div className="flappy-meta" style={{ marginTop: '0.75rem' }}>
              <div><strong>Your score:</strong> {myScore != null ? myScore : '—'}</div>
              <div><strong>Opponent score:</strong> {oppScore != null ? oppScore : '—'}</div>
            </div>
          )}

          {status === 'pending' && (
            <>
              <div className="flappy-hint">Share this match id with a friend: <strong>{match.id}</strong></div>

              {isRoomFullForViewer && (
                <div className="flappy-hint">Room is full. You are watching this match.</div>
              )}

              {canJoinOpenRoom && (
                <button
                  className="flappy-btn-primary"
                  style={{ marginTop: '0.75rem' }}
                  onClick={async () => {
                    try {
                      setError('');
                      await flappyAPI.joinMatch(match.id);
                      await load();
                      refreshUser?.();
                    } catch (e) {
                      const msg = String(e?.response?.data?.error || '').toLowerCase();
                      if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
                        setError('This room is full. You can watch the game live.');
                      } else {
                        setError(e.response?.data?.error || 'Failed to join room');
                      }
                    }
                  }}
                >
                  Join Challenge
                </button>
              )}

              {match.opponent_id && (
                <div className="flappy-meta" style={{ marginTop: '0.75rem' }}>
                  <div><strong>Accepted:</strong> {match.accepted_at ? 'Yes' : 'No'}</div>
                  <div><strong>Started:</strong> {match.started_at ? 'Yes' : 'No'}</div>
                </div>
              )}

              {match.opponent_id && !match.accepted_at && isOpponent && (
                <button
                  className="flappy-btn-primary"
                  style={{ marginTop: '0.75rem' }}
                  onClick={async () => {
                    try {
                      setError('');
                      await flappyAPI.acceptMatch(match.id);
                      await load();
                      refreshUser?.();
                    } catch (e) {
                      setError(e.response?.data?.error || 'Failed to accept');
                    }
                  }}
                >
                  Accept Challenge
                </button>
              )}

              {match.opponent_id && match.accepted_at && !match.started_at && isCreator && (
                <button
                  className="flappy-btn-primary"
                  style={{ marginTop: '0.75rem', fontSize: '1.1rem', gap: '8px', display: 'inline-flex', alignItems: 'center' }}
                  onClick={async () => {
                    try {
                      setError('');
                      await flappyAPI.startMatch(match.id);
                      await load();
                    } catch (e) {
                      setError(e.response?.data?.error || 'Failed to start');
                    }
                  }}
                >
                  ▶ Start Match
                </button>
              )}

              {match.opponent_id && match.accepted_at && !match.started_at && !isCreator && (
                <div className="flappy-hint">Waiting for the creator to click Start.</div>
              )}
            </>
          )}

          {status !== 'completed' && !myTurn && otherRun && (
            <div className="flappy-hint">Ghost available: {otherRun.player_username} scored {otherRun.score}</div>
          )}

          {status !== 'completed' && myTurn && otherRun && (
            <div className="flappy-hint">You can see your opponent's ghost while you play.</div>
          )}

          {status !== 'completed' && (myTurn || status === 'pending' || status === 'in_progress') && (
            <div className="flappy-row" style={{ marginTop: 10 }}>
              <label className="flappy-label">Ghost mode</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  checked={ghostEnabled}
                  onChange={(e) => setGhostEnabled(e.target.checked)}
                  disabled={submitting || submitted}
                />
                <div className="flappy-hint" style={{ margin: 0 }}>
                  {ghostEnabled ? 'On (show opponent ghost)' : 'Off (no ghost)'}
                </div>
              </div>
            </div>
          )}

          {status !== 'completed' && myRun && (
            <div className="flappy-hint">You already submitted this round: score {myRun.score}</div>
          )}
        </section>

        <section className="flappy-card">
          <h2>Round {currentRound}</h2>

          {!roundSeed && <div className="flappy-error">Round seed not available</div>}

          {roundSeed && (
            <>
              <FlappyCanvas
                seed={roundSeed}
                durationSeconds={durationSeconds}
                mode={(status === 'in_progress' && myTurn) ? 'play' : 'watch'}
                ghostRun={effectiveGhostRun}
                disabled={status !== 'in_progress' || !myTurn || submitted || submitting || !!myRun}
                allowPractice={status !== 'completed' && (!myTurn || status === 'pending')}
                onFinish={handleFinish}
                onFlap={({ tMs }) => {
                  if (status !== 'in_progress') return;
                  if (!myTurn) return;
                  if (!match?.id) return;
                  flappyDuelSocket.sendFlap({ matchId: match.id, roundNumber: currentRound, tMs });
                }}
              />

              {submitting && <div className="flappy-hint">Submitting run...</div>}
              {submitted && (
                <div className="flappy-hint">
                  {status !== 'in_progress'
                    ? 'Run submitted. Syncing match status...'
                    : (otherRun ? 'Run submitted. Calculating result...' : 'Run submitted. Waiting for opponent to play...')}
                </div>
              )}

              {!myTurn && status === 'in_progress' && (
                <div className="flappy-hint">It's not your turn yet. You can still watch when a ghost is available.</div>
              )}

              {status === 'pending' && match.opponent_id && match.accepted_at && !match.started_at && (
                <div className="flappy-hint">Match will start after the creator clicks Start.</div>
              )}
            </>
          )}
        </section>
      </div>

      <QuickChat 
        socket={flappyDuelSocket.socket} 
        roomId={match.id} 
        meId={user?.id} 
        gameType="flappy"
      />
    </div>
  );
}
